import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Hash, Plus, Copy, Pencil, Trash2, Search, Briefcase, User, MapPin, X, Loader2 } from "lucide-react";

export const SERIAL_STATUS = {
  available: { label: "Available", cls: "bg-emerald-50 text-emerald-700" },
  reserved: { label: "Reserved", cls: "bg-amber-50 text-amber-700" },
  incoming: { label: "Incoming", cls: "bg-violet-50 text-violet-700" },
  sold: { label: "Sold", cls: "bg-indigo-50 text-indigo-700" },
  installed: { label: "Installed", cls: "bg-sky-50 text-sky-700" },
  returned: { label: "Returned", cls: "bg-slate-100 text-slate-700" },
  quarantined: { label: "Quarantined", cls: "bg-rose-50 text-rose-700" },
};
const selCls = "w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm";
const EMPTY = { serial_number: "", status: "available", location_id: "", job_legacy_id: "", customer_id: "", order_ref: "",
  sale_ref: "", installation_ref: "", received_date: "", sold_date: "", installed_date: "", notes: "" };
const F = ({ label, children }) => <label className="block"><span className="text-[11px] font-medium text-slate-500">{label}</span><div className="mt-0.5">{children}</div></label>;

function CustomerPicker({ value, name, onChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => api.get("/contacts", { params: { q, page_size: 6 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) return (
    <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm" data-testid="serial-customer-selected">
      <span className="truncate flex-1">{name || value}</span>
      <button type="button" onClick={() => onChange("", "")} className="text-slate-400 hover:text-rose-600" data-testid="serial-customer-clear"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer…" className="h-8 text-sm" data-testid="serial-customer-search" />
      {items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {items.map((c) => (
            <button key={c.legacy_id} type="button" onClick={() => { onChange(c.legacy_id, c.full_name || c.company); setQ(""); setItems([]); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50" data-testid={`serial-customer-opt-${c.legacy_id}`}>
              {c.full_name || c.company || c.legacy_id}<span className="text-[11px] text-slate-400 ml-1">{c.postcode || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SerialEditor({ initial, locations, onSave, onCancel, busy, isNew }) {
  const [f, setF] = useState({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ""])) });
  const [custName, setCustName] = useState(initial?.customer_name || "");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const D = (k, label) => <F label={label}><Input type="date" value={(f[k] || "").slice(0, 10)} onChange={(e) => set(k, e.target.value)} className="h-8 text-sm" data-testid={`serial-${k}`} /></F>;
  const T = (k, label, ph) => <F label={label}><Input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="h-8 text-sm" data-testid={`serial-${k}`} /></F>;
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 mb-3" data-testid={isNew ? "serial-add-form" : "serial-edit-form"}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <F label="Serial number *"><Input value={f.serial_number} autoFocus={isNew} onChange={(e) => set("serial_number", e.target.value)} placeholder="Scan or type"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(f); } }} className="h-8 text-sm font-mono" data-testid="serial-number-input" /></F>
        <F label="Status"><select value={f.status} onChange={(e) => set("status", e.target.value)} className={selCls} data-testid="serial-status-input">
          {Object.entries(SERIAL_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></F>
        <F label="Stock location"><select value={f.location_id} onChange={(e) => set("location_id", e.target.value)} className={selCls} data-testid="serial-location">
          <option value="">Unassigned</option>{locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}</select></F>
        <F label="Customer"><CustomerPicker value={f.customer_id} name={custName} onChange={(id, n) => { set("customer_id", id); setCustName(n); }} /></F>
        {T("job_legacy_id", "Job ID", "e.g. JOB-1234")}
        {T("order_ref", "Order ref")}
        {T("sale_ref", "Sale ref")}
        {T("installation_ref", "Installation ref")}
        {D("received_date", "Date received")}
        {D("sold_date", "Date sold")}
        {D("installed_date", "Date installed")}
        <div className="sm:col-span-2">{T("notes", "Notes")}</div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="serial-cancel">Cancel</Button>
        <Button size="sm" disabled={busy} onClick={() => onSave(f)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="serial-save">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}{isNew ? "Add serial" : "Save"}</Button>
      </div>
    </div>
  );
}

function SerialRow({ s, canWrite, onEdit, onRemove }) {
  const st = SERIAL_STATUS[s.status] || { label: s.status, cls: "bg-slate-100 text-slate-600" };
  const copy = () => navigator.clipboard?.writeText(s.serial_number).then(() => toast.success("Serial copied")).catch(() => toast.error("Copy failed"));
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5" data-testid={`serial-row-${s.serial_id}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-sm text-slate-800 break-all" data-testid={`serial-number-${s.serial_id}`}>{s.serial_number}</span>
        <button onClick={copy} title="Copy" className="text-slate-400 hover:text-indigo-600" data-testid={`serial-copy-${s.serial_id}`}><Copy className="w-3.5 h-3.5" /></button>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`} data-testid={`serial-status-${s.serial_id}`}>{st.label}</span>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 flex-1 min-w-0">
        {s.location_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location_name}</span>}
        {s.job_legacy_id && <Link to={`/tasks/${s.job_legacy_id}`} className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 hover:bg-indigo-50 hover:text-indigo-700" data-testid={`serial-job-${s.serial_id}`}><Briefcase className="w-3 h-3" />{s.job_legacy_id}{s.job_title ? ` · ${s.job_title}` : ""}</Link>}
        {s.customer_id && <Link to={`/users/${s.customer_id}`} className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 hover:bg-indigo-50 hover:text-indigo-700" data-testid={`serial-customer-${s.serial_id}`}><User className="w-3 h-3" />{s.customer_name || s.customer_id}</Link>}
        {s.received_date && <span>Recv {s.received_date.slice(0, 10)}</span>}
        {(s.installed_date || s.sold_date) && <span>{s.installed_date ? `Installed ${s.installed_date.slice(0, 10)}` : `Sold ${s.sold_date.slice(0, 10)}`}</span>}
      </div>
      {canWrite && (
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={onEdit} title="Edit" className="p-1 text-slate-400 hover:text-indigo-600" data-testid={`serial-edit-${s.serial_id}`}><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={onRemove} disabled={!s.removable} title={s.removable ? "Remove" : "Only Available or Quarantined serials can be removed"}
            className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-slate-400" data-testid={`serial-remove-${s.serial_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export default function ProductSerials({ product, locations, canManageProduct, onEnabled }) {
  const { user, can } = useAuth();
  const canWrite = user?.is_super || (can && (can("stock.adjust") || can("stock.manage")));
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("serial") || "");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const pid = product.legacy_id;

  const load = () => api.get(`/products/${pid}/serials`, { params: { q: q || undefined, status: status || undefined } })
    .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { if (!product.serialized) return; const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [pid, q, status, product.serialized]);

  const enable = async () => {
    try { await api.patch(`/products/${pid}`, { serialized: true }); toast.success("Serial numbers enabled"); onEnabled?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not enable"); }
  };
  if (!product.serialized) {
    return (
      <section className="bg-white border border-dashed border-slate-300 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="product-serials-disabled">
        <div className="flex items-center gap-2 text-sm text-slate-600"><Hash className="w-4 h-4 text-purple-500" /> Serial numbers are off for this product.</div>
        {canManageProduct && <Button size="sm" variant="outline" onClick={enable} data-testid="serial-enable-btn">Enable serial numbers</Button>}
      </section>
    );
  }

  const save = async (f) => {
    if (!f.serial_number.trim()) { toast.error("Serial number required"); return; }
    setBusy(true);
    const body = Object.fromEntries(Object.entries(f).filter(([k]) => k in EMPTY));
    try {
      if (mode === "new") {
        const clean = Object.fromEntries(Object.entries(body).map(([k, v]) => [k, v === "" ? null : v]));
        await api.post("/stock/serials", { ...clean, status: f.status, product_legacy_id: pid });
        toast.success("Serial added");
      } else {
        await api.patch(`/stock/serials/${mode.serial_id}`, body);
        toast.success("Serial updated");
      }
      setMode(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save serial"); }
    finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove serial ${s.serial_number}? This is recorded in the activity log.`)) return;
    try { await api.delete(`/stock/serials/${s.serial_id}`); toast.success("Serial removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove"); }
  };

  const counts = (items || []).reduce((m, s) => ({ ...m, [s.status]: (m[s.status] || 0) + 1 }), {});
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="product-serials">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Hash className="w-4 h-4 text-purple-500" /> Serial Numbers <span className="text-slate-400 font-medium" data-testid="serial-count">({items ? items.length : "…"})</span></h2>
        {canWrite && mode !== "new" && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setMode("new")} data-testid="serial-add-toggle"><Plus className="w-4 h-4" /> Add serial number</Button>}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial number…" className="pl-8 h-8 text-sm" data-testid="serial-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 h-8 text-sm" data-testid="serial-status-filter">
          <option value="">All statuses</option>
          {Object.entries(SERIAL_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}{counts[k] && !status ? ` (${counts[k]})` : ""}</option>)}
        </select>
      </div>
      {mode === "new" && <SerialEditor isNew locations={locations} busy={busy} onSave={save} onCancel={() => setMode(null)} />}
      {items === null ? <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : items.length === 0 ? <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200" data-testid="serial-empty">{q || status ? "No serial numbers match." : "No serial numbers recorded."}</div>
        : (
          <div className="divide-y divide-slate-100">
            {items.map((s) => (mode?.serial_id === s.serial_id
              ? <SerialEditor key={s.serial_id} initial={s} locations={locations} busy={busy} onSave={save} onCancel={() => setMode(null)} />
              : <SerialRow key={s.serial_id} s={s} canWrite={canWrite} onEdit={() => setMode(s)} onRemove={() => remove(s)} />))}
          </div>
        )}
    </section>
  );
}
